import React, { useEffect, useRef } from "react";
import { Check, Plus } from "lucide-react";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useIntegrations, useSaveIntegrations } from "../../hooks/useApi";
import { useControlloLimite } from "../billing/AvvisoLimitePiano";
import { apriPiani } from "../../utils/limitiPiano";

// Le app che il piano puo' non comprendere, con la funzione del piano.
const FUNZIONE_DELL_APP = { whatsapp: "whatsapp_integration", contact_form: "contact_forms" };

/**
 * Salvataggio delle impostazioni di un'app, con il messaggio di esito.
 * Il server risponde con i motivi quando rifiuta (numero non valido, modulo
 * senza campi): si mostrano cosi' come sono.
 * Accendere un'app che il piano non comprende (available false), per chi puo'
 * comprare, apre i piani al posto di accenderla: quello che e' scritto si
 * salva lo stesso, spento. Chi non puo' comprare la accende come prima (in
 * chat non compare finche' il piano non la prevede, lo dice la pagina).
 */
export function useSalvaIntegrazioni(botId) {
  const { toast } = useToast();
  const mutation = useSaveIntegrations(botId);
  const { data } = useIntegrations(botId);
  const { puoComprare } = useControlloLimite();

  const salva = async (changes, okTitle) => {
    const fuori = Object.keys(changes).find(
      (chiave) =>
        changes[chiave]?.enabled === true &&
        data?.[chiave]?.available === false &&
        FUNZIONE_DELL_APP[chiave] &&
        puoComprare({ feature: FUNZIONE_DELL_APP[chiave] })
    );
    if (fuori) {
      const { enabled, ...resto } = changes[fuori];
      const senzaAccendere = { ...changes, [fuori]: resto };
      if (!Object.keys(resto).length) delete senzaAccendere[fuori];
      if (Object.keys(senzaAccendere).length) await salva(senzaAccendere, null);
      apriPiani({ feature: FUNZIONE_DELL_APP[fuori] });
      return false;
    }
    try {
      await mutation.mutateAsync(changes);
      if (okTitle) toast({ title: okTitle });
      return true;
    } catch (error) {
      const errors = error?.data?.errors || error?.response?.data?.errors;
      toast({
        title: "Non salvato",
        description: errors?.length ? errors.join(" ") : error?.message || "Riprova fra poco.",
        variant: "destructive",
      });
      return false;
    }
  };

  return { salva, saving: mutation.isPending };
}

/**
 * Fa sapere all'intestazione della pagina (IntegrationDetailPage) se l'app si
 * puo' accendere con quello che c'e' scritto adesso, anche se non e' ancora
 * salvato, e come: `attiva` salva le modifiche e accende insieme.
 * Senza, l'interruttore guarderebbe solo i dati gia' salvati e resterebbe
 * spento dopo aver scritto il numero o i campi.
 */
export function useAttivazione(onAttivazione, pronta, attiva) {
  const ultima = useRef(attiva);
  ultima.current = attiva;
  useEffect(() => {
    onAttivazione?.({ pronta, attiva: () => ultima.current() });
  }, [onAttivazione, pronta]);
  useEffect(() => () => onAttivazione?.(null), [onAttivazione]);
}

/**
 * Il "quando" di un'app: il testo che il bot legge per decidere. Gli esempi
 * del registro si aggiungono con un clic.
 */
export function QuandoUsarla({ id, value, onChange, placeholder, max, examples = [] }) {
  const aggiungi = (esempio) => {
    const attuale = value.trim();
    if (attuale.includes(esempio)) return;
    onChange((attuale ? `${attuale} ${esempio}` : esempio).slice(0, max));
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-xs font-medium text-[#334155]">
        Quando deve usarla l'assistente
      </Label>
      <Textarea
        id={id}
        rows={3}
        maxLength={max}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="resize-none bg-white text-sm focus-visible:ring-0 focus-visible:border-[#CBD5E1]"
      />
      <div className="flex justify-between gap-4 text-xs text-[#64748B]">
        <span>
          Descrivi in quali situazioni l'assistente deve proporla. Se lasci il campo vuoto, segue il
          testo suggerito.
        </span>
        <span className="whitespace-nowrap text-[#94A3B8]">
          {value.length}/{max}
        </span>
      </div>
      {examples.length ? (
        <div className="flex flex-wrap gap-2 pt-1">
          {examples.map((esempio) => {
            const usato = value.includes(esempio);
            return (
              <button
                key={esempio}
                type="button"
                onClick={() => aggiungi(esempio)}
                disabled={usato}
                className="inline-flex items-center gap-1 rounded-full border border-[#E2E8F0] bg-white px-3 py-1 text-xs text-[#334155] transition-colors hover:border-violet-300 hover:bg-violet-50/60 disabled:cursor-default disabled:border-violet-200 disabled:bg-violet-50/60 disabled:text-violet-700"
              >
                {usato ? (
                  <Check className="h-3 w-3" aria-hidden="true" />
                ) : (
                  <Plus className="h-3 w-3 text-violet-500" aria-hidden="true" />
                )}
                {esempio}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
